/**
 * A unified diff a person can write notes on (D341). A gutter button on every changed or context line opens a small
 * composer; dragging down the gutter makes a range; `N` on a focused line does the same from the keyboard. Notes sit
 * under the last line they cover, as drafts until sent, then as sent, done or skipped from the agent's answer; resolved
 * ones fold into one line. The diff, the pull request's files and the task diff all draw their lines through this.
 */

import { ArrowRotateLeft, Check, ChevronsExpandVertical, Pencil, Plus, TrashBin } from '@gravity-ui/icons';
import { Button, TextArea } from '@heroui/react';
import { type ReactNode,useCallback, useEffect, useMemo, useState } from 'react';

import { type DiffRow, isResolved, noteAddress, noteCoversLine, type NoteInput, type NoteSide, type ReviewNoteItem } from '@aioe/shared';
import { type Fold, foldLabel, foldRuns } from './foldModel.js';
import { type Token, tokenizeRows } from './syntax.js';

export interface ReviewPatchProps {
  rows: DiffRow[];
  /** The file the rows belong to when they carry no `diff --git` header (a pull request's single file). */
  defaultPath?: string;
  notes: ReviewNoteItem[];
  onAdd(input: NoteInput): void;
  onEdit(id: string, text: string): void;
  onRemove(id: string): void;
  onReopen(id: string): void;
  onResolve(id: string): void;
  /** Ctrl+Enter anywhere in the diff. */
  onSendBatch(): void;
  /** A badge beside a file's header row (its open note count). */
  fileBadge?: (path: string) => ReactNode;
  /** Fold runs of unchanged lines into "Show N unchanged lines" rows (a diff with whole-file context). */
  fold?: boolean;
  /** Leave out the diff's own header and hunk lines: the file's name is shown elsewhere. */
  hideHeaders?: boolean;
  /** Colour the code by its file's language (default on). */
  syntax?: boolean;
}

interface Anchor {
  path: string;
  side: NoteSide;
  line: number;
  endLine?: number;
  /** The row the composer sits under: the last line of the range. */
  afterIndex: number;
}

const sideOf = (row: DiffRow): NoteSide => (row.kind === 'del' ? 'old' : 'new');
const lineOf = (row: DiffRow, side: NoteSide): number | undefined => (side === 'old' ? row.oldNo : row.newNo);
const annotatable = (row: DiffRow): boolean => row.kind === 'add' || row.kind === 'del' || row.kind === 'context';

const STATUS_WORDS: Record<ReviewNoteItem['status'], string> = { draft: 'Draft', sent: 'Sent, waiting for the agent', done: 'Done', skipped: 'Skipped' };

function NoteCard({ note, props }: { note: ReviewNoteItem; props: Pick<ReviewPatchProps, 'onEdit' | 'onRemove' | 'onReopen' | 'onResolve'> }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.text);
  const [open, setOpen] = useState(false);
  const resolved = isResolved(note);
  if (resolved && !open) {
    return (
      <button type="button" className="review-note review-note--folded" data-status={note.status} onClick={() => setOpen(true)} aria-expanded={false}>
        <Check className="size-3 shrink-0" aria-hidden />
        <span className="shrink-0">{STATUS_WORDS[note.status]}</span>
        <span className="min-w-0 flex-1 truncate text-muted" title={`${noteAddress(note)} — ${note.text}`}>
          {noteAddress(note)} — {note.text}
        </span>
      </button>
    );
  }
  return (
    <div className="review-note" data-status={note.status} role="group" aria-label={`Note on ${noteAddress(note)}`}>
      <div className="review-note__meta">
        <span className="review-note__status">{STATUS_WORDS[note.status]}</span>
        <span className="min-w-0 flex-1 truncate font-mono text-muted" title={noteAddress(note)}>{noteAddress(note)}</span>
        {note.sentTo && note.status !== 'draft' ? <span className="text-muted">to {note.sentTo}</span> : null}
      </div>
      {editing ? (
        <TextArea
          autoFocus
          aria-label="Edit note"
          rows={2}
          className="w-full resize-y text-xs"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') {
              setText(note.text);
              setEditing(false);
            } else if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
              e.preventDefault();
              props.onEdit(note.id, text);
              setEditing(false);
            }
          }}
        />
      ) : (
        <p className="review-note__text">{note.text}</p>
      )}
      {note.reply ? <p className="review-note__reply">The agent: {note.reply}</p> : null}
      <div className="review-note__actions">
        {note.status === 'draft' && !editing ? (
          <>
            <Button size="sm" variant="ghost" className="h-6 min-w-0 px-2 text-xs" onPress={() => setEditing(true)}>
              <Pencil className="size-3" aria-hidden /> Edit
            </Button>
            <Button size="sm" variant="ghost" className="h-6 min-w-0 px-2 text-xs" onPress={() => props.onRemove(note.id)}>
              <TrashBin className="size-3" aria-hidden /> Delete
            </Button>
          </>
        ) : null}
        {editing ? (
          <>
            <Button size="sm" variant="secondary" className="h-6 px-2 text-xs" onPress={() => { props.onEdit(note.id, text); setEditing(false); }}>
              Save
            </Button>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onPress={() => { setText(note.text); setEditing(false); }}>
              Cancel
            </Button>
          </>
        ) : null}
        {note.status === 'sent' ? (
          <>
            <Button size="sm" variant="ghost" className="h-6 min-w-0 px-2 text-xs" onPress={() => props.onReopen(note.id)}>
              <ArrowRotateLeft className="size-3" aria-hidden /> Send again
            </Button>
            <Button size="sm" variant="ghost" className="h-6 min-w-0 px-2 text-xs" onPress={() => props.onResolve(note.id)}>
              <Check className="size-3" aria-hidden /> Mark done
            </Button>
          </>
        ) : null}
        {resolved ? (
          <>
            <Button size="sm" variant="ghost" className="h-6 min-w-0 px-2 text-xs" onPress={() => props.onReopen(note.id)}>
              <ArrowRotateLeft className="size-3" aria-hidden /> Send again
            </Button>
            <Button size="sm" variant="ghost" className="h-6 min-w-0 px-2 text-xs" onPress={() => setOpen(false)}>
              Fold
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Composer({ anchor, onSubmit, onCancel }: { anchor: Anchor; onSubmit: (text: string) => void; onCancel: () => void }) {
  const [text, setText] = useState('');
  const range = anchor.endLine && anchor.endLine !== anchor.line ? `${anchor.line}-${anchor.endLine}` : `${anchor.line}`;
  return (
    <div className="review-composer" role="group" aria-label={`New note on ${anchor.path} line ${range}`}>
      <div className="review-note__meta">
        <span className="review-note__status">New note</span>
        <span className="min-w-0 flex-1 truncate font-mono text-muted" title={noteAddress(anchor)}>{noteAddress(anchor)}</span>
      </div>
      <TextArea
        autoFocus
        aria-label="Note"
        rows={2}
        className="w-full resize-y text-xs"
        placeholder="What should change here? Enter adds it, Esc cancels."
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onCancel();
          } else if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            onSubmit(text);
          }
        }}
      />
      <div className="review-note__actions">
        <Button size="sm" variant="secondary" className="h-6 px-2 text-xs" isDisabled={!text.trim()} onPress={() => onSubmit(text)}>
          Add note
        </Button>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onPress={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Colored({ tokens, text }: { tokens: Token[] | undefined; text: string }) {
  if (!tokens) return <>{text}</>;
  return (
    <>
      {tokens.map((t, i) => (t.kind ? <span key={i} className={`tok tok-${t.kind}`}>{t.text}</span> : t.text))}
    </>
  );
}

const NO_FOLDS: ReadonlySet<number> = new Set();

export function ReviewPatch(props: ReviewPatchProps) {
  const { rows, defaultPath, notes } = props;
  const [composer, setComposer] = useState<Anchor | null>(null);
  // Folds the reader opened, for these rows: a new diff folds again.
  const [opened, setOpened] = useState<{ rows: DiffRow[]; from: ReadonlySet<number> }>({ rows, from: NO_FOLDS });
  const openFolds = opened.rows === rows ? opened.from : NO_FOLDS;
  const tokens = useMemo(() => (props.syntax === false ? [] : tokenizeRows(rows, defaultPath ?? '')), [rows, defaultPath, props.syntax]);
  const [drag, setDrag] = useState<{ index: number; side: NoteSide; path: string; current: number } | null>(null);
  const pathOf = useCallback((r: DiffRow) => r.path ?? defaultPath ?? '', [defaultPath]);

  // Where each note sits: under the row that shows the last line it covers.
  const { under, orphans } = useMemo(() => {
    const map = new Map<number, ReviewNoteItem[]>();
    const lost: ReviewNoteItem[] = [];
    for (const n of notes) {
      const last = n.endLine ?? n.line;
      const at = rows.findIndex((r) => annotatable(r) && pathOf(r) === n.path && lineOf(r, n.side) === last);
      if (at < 0) lost.push(n);
      else map.set(at, [...(map.get(at) ?? []), n]);
    }
    return { under: map, orphans: lost };
  }, [notes, rows, pathOf]);

  // Runs of unchanged lines that are folded away: a line with a note stays visible, and so does the one a composer sits under.
  const folds = useMemo(() => {
    if (!props.fold) return new Map<number, Fold>();
    const pinned = new Set<number>(under.keys());
    const found = foldRuns(rows, { pinned });
    return new Map(found.filter((f) => !openFolds.has(f.from)).map((f) => [f.from, f] as const));
  }, [props.fold, rows, under, openFolds]);
  const hiddenBy = useMemo(() => {
    const hidden = new Set<number>();
    for (const f of folds.values()) for (let i = f.from; i <= f.to; i++) hidden.add(i);
    return hidden;
  }, [folds]);

  const open = useCallback(
    (index: number, endIndex = index) => {
      const a = rows[index];
      const b = rows[endIndex];
      if (!a || !b || !annotatable(a) || !annotatable(b)) return;
      const side = sideOf(a);
      const x = lineOf(a, side);
      const y = lineOf(b, side);
      if (x === undefined || y === undefined) return;
      setComposer({ path: pathOf(a), side, line: Math.min(x, y), ...(x !== y ? { endLine: Math.max(x, y) } : {}), afterIndex: Math.max(index, endIndex) });
    },
    [rows, pathOf],
  );

  // A drag ends wherever the pointer is let go.
  useEffect(() => {
    if (!drag) return;
    const d = drag;
    const up = () => {
      setDrag(null);
      open(d.index, d.current);
    };
    window.addEventListener('pointerup', up, { once: true });
    return () => window.removeEventListener('pointerup', up);
  }, [drag, open]);

  const inDrag = (i: number): boolean => !!drag && i >= Math.min(drag.index, drag.current) && i <= Math.max(drag.index, drag.current) && annotatable(rows[i]) && sideOf(rows[i]) === drag.side;

  return (
    <div
      className="review-patch"
      data-large={rows.length > 800 || undefined}
      role="table"
      aria-label="Diff with notes"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          props.onSendBatch();
        }
      }}
    >
      {orphans.length ? (
        <div className="review-orphans">
          <p className="m-0 text-xs text-muted">Notes on lines this diff does not show:</p>
          {orphans.map((n) => (
            <NoteCard key={n.id} note={n} props={props} />
          ))}
        </div>
      ) : null}
      {rows.map((r, i) => {
        const fold = folds.get(i);
        if (fold) {
          return (
            <button key={i} type="button" className="review-patch__fold" onClick={() => setOpened({ rows, from: new Set([...openFolds, fold.from]) })}>
              <ChevronsExpandVertical className="size-3" aria-hidden /> {foldLabel(fold.count)}
            </button>
          );
        }
        if (hiddenBy.has(i)) return null;
        if (props.hideHeaders && (r.kind === 'meta' || r.kind === 'hunk')) return null;
        const can = annotatable(r);
        const side = sideOf(r);
        const line = lineOf(r, side);
        const noted = can && line !== undefined && notes.some((n) => !isResolved(n) && noteCoversLine(n, pathOf(r), side, line));
        const composerHere = composer && composer.afterIndex === i;
        return (
          <div key={i} className="contents">
            <div
              className="review-patch__line"
              data-kind={r.kind}
              data-noted={noted || undefined}
              data-dragging={inDrag(i) || undefined}
              role="row"
              tabIndex={can ? 0 : undefined}
              onKeyDown={(e) => {
                if (can && (e.key === 'n' || e.key === 'N') && !e.ctrlKey && !e.metaKey && !e.altKey && e.target === e.currentTarget) {
                  e.preventDefault();
                  open(i);
                }
              }}
              onPointerEnter={() => {
                if (drag && can && sideOf(r) === drag.side && pathOf(r) === drag.path && lineOf(r, drag.side) !== undefined) setDrag({ ...drag, current: i });
              }}
            >
              {can ? (
                <button
                  type="button"
                  className="review-patch__add"
                  aria-label={`Add a note on line ${line}`}
                  tabIndex={-1}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    if (line !== undefined) setDrag({ index: i, side, path: pathOf(r), current: i });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      open(i);
                    }
                  }}
                >
                  <Plus className="size-3" aria-hidden />
                </button>
              ) : (
                <span className="review-patch__add" aria-hidden />
              )}
              <span className="review-patch__no" aria-hidden="true">{r.oldNo ?? ''}</span>
              <span className="review-patch__no" aria-hidden="true">{r.newNo ?? ''}</span>
              <span className="review-patch__text">
                {r.kind === 'add' ? '+' : r.kind === 'del' ? '-' : r.kind === 'context' ? ' ' : ''}
                <Colored tokens={tokens[i]} text={r.text} />
                {r.kind === 'meta' && r.text.startsWith('diff --git ') ? <FileBadge text={r.text} badge={props.fileBadge} /> : null}
              </span>
            </div>
            {(under.get(i) ?? []).map((n) => (
              <NoteCard key={n.id} note={n} props={props} />
            ))}
            {composerHere ? (
              <Composer
                anchor={composer}
                onCancel={() => setComposer(null)}
                onSubmit={(text) => {
                  if (!text.trim()) return;
                  props.onAdd({ path: composer.path, side: composer.side, line: composer.line, ...(composer.endLine ? { endLine: composer.endLine } : {}), text });
                  setComposer(null);
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function FileBadge({ text, badge }: { text: string; badge?: (path: string) => ReactNode }) {
  const m = /^diff --git a\/(.*) b\/(.*)$/.exec(text);
  if (!m || !badge) return null;
  return <span className="review-patch__badge">{badge(m[2])}</span>;
}
